/**
 * P2-07: ResourceTable —— 主资源表（迁移自 WResourceTable，js/jsx/left.jsx.js:564-671）。
 *
 * - 顺序：旧版**不做任何排序**，按 resPool.resources + getPseudoResources() 的
 *   引擎顺序原样迭代（left.jsx.js:577-589, 843-850；已确认全仓库无排序逻辑），
 *   此处保持一致；
 * - 折叠/编辑为组件 state，不持久化（left.jsx.js:568-573，与旧版一致）；
 * - 隐藏资源设置：编辑模式行级复选框（ResourceRow）+ 底部
 *   "Show hidden resources" 复选框直写 game.resPool.showHiddenResources
 *   （left.jsx.js:641-650, 668-670）；
 * - 字号缩放：zoomUp/zoomDown 链接仍直调引擎 game.ui.zoomUp/zoomDown
 *   （js/ui.js:871-893，写 #leftColumn 外壳 font-size），不在 React 内重新实现；
 * - 工具条问号沿用旧 WTooltip 自绘结构（.tooltip-block > .tooltip-content，
 *   left.jsx.js:952-993），不走 TooltipProvider——与旧版结构/class 逐一对齐；
 * - 全部旧 class 钩子保留（res-toolbar left/right、link collapse/toggled、
 *   svg-icon down/gear/question-mark、res-table、res-toggle-hidden）。
 */
import { useCallback, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { ResourceRow } from "./ResourceRow";
import type { ResLike } from "./ResourceRow";

// ---------------------------------------------------------------------------
// 表级快照：资源名序列（引擎顺序）+ showHiddenResources + 时间悖论 + reqRes
// ---------------------------------------------------------------------------

interface TableSnapshot {
  names: string[];
  showHidden: boolean;
  paradox: boolean;
  reqRes: string[];
}

function stringArrayEq(a: string[], b: string[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function tableSnapshotEq(a: TableSnapshot | null, b: TableSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.showHidden === b.showHidden &&
    a.paradox === b.paradox &&
    stringArrayEq(a.names, b.names) &&
    stringArrayEq(a.reqRes, b.reqRes)
  );
}

export function computeTableSnapshot(game: GamePage | null): TableSnapshot | null {
  if (!game) {
    return null;
  }
  const names = (game.resPool.resources as ResLike[])
    .map((r) => r.name)
    .concat((game.resPool.getPseudoResources() as ResLike[]).map((p) => p.name));
  return {
    names,
    showHidden: !!game.resPool.showHiddenResources,
    paradox: game.calendar.day < 0,
    reqRes: game.getRequiredResources(game.selectedBuilding) as string[],
  };
}

// ---------------------------------------------------------------------------
// WTooltip 迁移（left.jsx.js:952-993）：hover/Enter 切换的自绘 tooltip
// ---------------------------------------------------------------------------

interface InlineTipProps {
  body?: ReactNode;
  tabIndex?: number;
  children?: ReactNode;
}

function InlineTip({ body, tabIndex = 0, children }: InlineTipProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      tabIndex={tabIndex}
      className="tooltip-block"
      onMouseOver={() => setShowTooltip(true)}
      onMouseOut={() => setShowTooltip(false)}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter") {
          setShowTooltip(!showTooltip);
        }
      }}
    >
      {body ?? <div className="tooltip-icon">[?]</div>}
      {showTooltip && <div className="tooltip-content">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResourceTable
// ---------------------------------------------------------------------------

export function ResourceTable() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const selector = useCallback((game: GamePage | null) => computeTableSnapshot(game), []);
  const snap = useGameTick(selector, tableSnapshotEq);

  if (!snap) {
    return null;
  }

  const toggleEdit = () => setIsEditMode(!isEditMode);
  const toggleCollapsed = () => setIsCollapsed(!isCollapsed);

  return (
    <div aria-label="Regular resources">
      <div>
        <div className="res-toolbar left">
          <a
            href="#!"
            className="link collapse"
            onClick={toggleCollapsed}
            tabIndex={1}
            title="Toggle resources"
          >
            {isCollapsed ? ">(" + t("left.resources") + ")" : <div className="svg-icon down" />}
          </a>
        </div>
        <div className="res-toolbar right">
          <a
            className={"link" + (isEditMode ? " toggled" : "")}
            onClick={toggleEdit}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                toggleEdit();
              }
            }}
            title="Resource settings"
            tabIndex={1}
          >
            <div className="svg-icon gear" />
          </a>
          <InlineTip body={<div className="svg-icon question-mark" />} tabIndex={1}>
            {t("left.resources.tip")}
          </InlineTip>
        </div>
      </div>
      {!isCollapsed && (
        <div>
          {isEditMode && (
            <div style={{ textAlign: "right" }}>
              {/* 字号缩放直调引擎：写 #leftColumn 外壳 font-size（js/ui.js:891-893），
                  新岛位于外壳内部，经继承受影响，无需桥接 */}
              <a className="link" onClick={() => getGame()?.ui.zoomUp()}>
                {t("left.font.inc")}
              </a>
              <a className="link" onClick={() => getGame()?.ui.zoomDown()}>
                {t("left.font.dec")}
              </a>
            </div>
          )}
          <div className="res-table" role="grid">
            {snap.names.map((name) => (
              <ResourceRow
                key={name}
                name={name}
                isEditMode={isEditMode}
                isRequired={snap.reqRes.indexOf(name) >= 0}
                showHiddenResources={snap.showHidden}
                isTemporalParadox={snap.paradox}
              />
            ))}
          </div>
        </div>
      )}
      {!isCollapsed && isEditMode && (
        <div className="res-toggle-hidden">
          <input
            type="checkbox"
            checked={snap.showHidden}
            onChange={(e) => {
              const game = getGame();
              if (game) {
                game.resPool.showHiddenResources = e.target.checked;
              }
            }}
            style={{ display: "inline-block" }}
          />
          {t("res.show.hidden")}
        </div>
      )}
    </div>
  );
}

/**
 * 独立挂载入口（自带 TooltipProvider），仅供隔离测试/调试。
 * P2-20 起生产挂载由 LeftPanel 统一承担（Provider 已上提到 LeftPanel 根），
 * 本入口不再注册到 KGNewUI.panels。
 */
export default function ResourceTablePanel() {
  return (
    <TooltipProvider>
      <ResourceTable />
    </TooltipProvider>
  );
}
